export type ModalOrigin = {
  x: number;
  y: number;
  width: number;
  height: number;
  /** Live element to fly back to, via `[data-tx-anchor]`. */
  anchorId?: string;
  /**
   * Element that opened the dialog. The same transaction can be mounted in
   * more than one list, so the flight and the land pulse use this node when
   * it is still connected.
   */
  anchorEl?: HTMLElement;
};

const IDENTITY = "none";

export type OriginMotionHandle = {
  animations: Animation[];
  finished: Promise<void>;
};

export function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function readElementOrigin(
  el: HTMLElement | null,
  anchorId?: string
): ModalOrigin | undefined {
  if (!el) return undefined;
  const rect = el.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return undefined;
  return {
    x: rect.left,
    y: rect.top,
    width: rect.width,
    height: rect.height,
    anchorId,
    anchorEl: el,
  };
}

function anchorElement(origin: Pick<ModalOrigin, "anchorId" | "anchorEl">) {
  const live = origin.anchorEl;
  if (live?.isConnected) {
    const anchorId = live.getAttribute("data-tx-anchor");
    if (!origin.anchorId || anchorId === origin.anchorId) return live;
  }
  if (typeof document === "undefined" || !origin.anchorId) return null;
  try {
    const found = document.querySelector(
      `[data-tx-anchor="${CSS.escape(origin.anchorId)}"]`
    );
    return found instanceof HTMLElement ? found : null;
  } catch {
    return null;
  }
}

function resolveModalOrigin(origin: ModalOrigin): ModalOrigin {
  const el = anchorElement(origin);
  if (!el) return origin;
  const rect = el.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return origin;
  if (rect.bottom < 0 || rect.top > window.innerHeight) return origin;
  return {
    ...origin,
    x: rect.left,
    y: rect.top,
    width: rect.width,
    height: rect.height,
  };
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function motionFrames(
  panel: DOMRect,
  origin: ModalOrigin,
  direction: "enter" | "leave"
): Keyframe[] {
  const dx = origin.x + origin.width / 2 - (panel.left + panel.width / 2);
  const dy = origin.y + origin.height / 2 - (panel.top + panel.height / 2);
  const scaleX = clamp(origin.width / Math.max(panel.width, 1), 0.06, 1);
  const scaleY = clamp(origin.height / Math.max(panel.height, 1), 0.06, 1);
  const start = `translate3d(${dx.toFixed(2)}px, ${dy.toFixed(2)}px, 0) scale(${scaleX.toFixed(4)}, ${scaleY.toFixed(4)})`;

  if (direction === "leave") {
    return [{ transform: IDENTITY }, { transform: start }];
  }

  // One segment only. Ending on `none` matches the panel's resting CSS, so
  // releasing the animation does not nudge or flash it into place.
  return [{ transform: start }, { transform: IDENTITY }];
}

export function playModalOriginMotion(
  panel: HTMLElement,
  origin: ModalOrigin,
  direction: "enter" | "leave"
): OriginMotionHandle | null {
  const rect = panel.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return null;

  const live = resolveModalOrigin(origin);
  const duration = direction === "enter" ? 380 : 320;
  panel.setAttribute("data-origin-motion", direction);

  const panelAnimation = panel.animate(
    motionFrames(rect, live, direction),
    {
      duration,
      fill: "both",
      easing:
        direction === "enter"
          ? "cubic-bezier(0.2, 0.85, 0.2, 1)"
          : "cubic-bezier(0.4, 0, 0.2, 1)",
    }
  );

  const contentFrames: Keyframe[] =
    direction === "enter"
      ? [
          { opacity: 0, offset: 0 },
          { opacity: 1, offset: 0.45 },
          { opacity: 1, offset: 1 },
        ]
      : [
          { opacity: 1, offset: 0 },
          { opacity: 0, offset: 0.35 },
          { opacity: 0, offset: 1 },
        ];

  const contentNodes = Array.from(
    panel.querySelectorAll<HTMLElement>("[data-origin-fade]")
  );
  if (direction === "enter") {
    contentNodes.forEach((node) => {
      node.style.opacity = "0";
    });
  }
  const contentAnimations = contentNodes.map((node) =>
    node.animate(contentFrames, { duration, fill: "both", easing: "linear" })
  );

  const animations = [panelAnimation, ...contentAnimations];
  return {
    animations,
    finished: panelAnimation.finished.then(
      () => undefined,
      () => undefined
    ),
  };
}

export function clearModalOriginMotion(
  panel: HTMLElement,
  motion: OriginMotionHandle | null
) {
  // Drop the inline opacity before cancelling. The enter flight sets it to 0
  // underneath the animation; cancelling first would flash the content out.
  panel.querySelectorAll<HTMLElement>("[data-origin-fade]").forEach((node) => {
    node.style.opacity = "";
  });
  motion?.animations.forEach((animation) => {
    try {
      animation.cancel();
    } catch {
      // Already finished or cancelled.
    }
  });
  panel.style.transform = "";
  panel.removeAttribute("data-origin-motion");
}

function restartMotionClass(el: HTMLElement, className: string) {
  el.classList.remove(className);
  void el.offsetWidth;
  el.classList.add(className);
  const done = (event: AnimationEvent) => {
    if (event.target !== el) return;
    el.classList.remove(className);
    el.removeEventListener("animationend", done);
  };
  el.addEventListener("animationend", done);
}

export function markTransactionOriginLaunch(el: HTMLElement | null) {
  if (!el || prefersReducedMotion()) return;
  el.classList.remove("tx-origin-land");
  restartMotionClass(el, "tx-origin-launch");
}

export function markTransactionOriginLand(
  origin?: Pick<ModalOrigin, "anchorId" | "anchorEl">
) {
  if (!origin || prefersReducedMotion() || typeof document === "undefined") {
    return;
  }
  const el = anchorElement(origin);
  if (!el) return;
  el.classList.remove("tx-origin-launch");
  restartMotionClass(el, "tx-origin-land");
}
